import { useEffect, useState } from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import type { SubmissionAnswer } from "../../types/evidence";

export default function SubmissionAnswerRow({ field, answer, disabled, onSave, onEditingChange }: {
  field: string;
  answer: SubmissionAnswer;
  disabled: boolean;
  onSave: (text: string | null) => Promise<boolean>;
  onEditingChange: (field: string, editing: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(answer.text);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setText(answer.text); }, [answer.text, editing]);
  // Text in the box is not what Shopify would get: the column holds Submit until it is saved or dropped.
  useEffect(() => { onEditingChange(field, editing); return () => onEditingChange(field, false); }, [field, editing, onEditingChange]);

  const save = async (value: string | null) => {
    if (value === answer.text) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(value);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Box sx={{ py: 1, borderTop: 1, borderColor: "divider" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{answer.label}</Typography>
        {answer.edited && <Chip label="edited" size="small" variant="outlined" sx={{ height: 18, fontSize: "0.7rem" }} />}
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto !important", whiteSpace: "nowrap" }}>{`from ${answer.source}`}</Typography>
      </Stack>
      {editing ? (
        <>
          <TextField value={text} onChange={(e) => setText(e.target.value)} multiline minRows={3} fullWidth size="small" sx={{ mt: 1 }} disabled={saving} />
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Button size="small" onClick={() => void save(text)} disabled={saving}>Save</Button>
            <Button size="small" onClick={() => { setEditing(false); setText(answer.text); }} disabled={saving}>Cancel</Button>
            {answer.edited && <Button size="small" onClick={() => void save(null)} disabled={saving}>Back to the document's text</Button>}
          </Stack>
        </>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, whiteSpace: "pre-line", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{answer.text}</Typography>
          <Button size="small" variant="text" onClick={() => setEditing(true)} disabled={disabled} sx={{ flexShrink: 0 }}>Edit</Button>
        </Box>
      )}
    </Box>
  );
}
